// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

// Material UI
import {
    Box,
    Button,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from "@mui/material";
import Slider, { SliderThumb } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';

// Icons
import CircleTwoToneIcon from '@mui/icons-material/CircleTwoTone';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import ClosedCaptionDisabledIcon from '@mui/icons-material/ClosedCaptionDisabled';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import PlayCircleFilledTwoToneIcon from '@mui/icons-material/PlayCircleFilledTwoTone';
import Replay10Icon from '@mui/icons-material/Replay10';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';

// Custom Components
import IconToggleButton from '../utils/IconToggleButton';
import RenderIf from '../utils/RenderIf';

// Custom Scripts
import { hardLineGradient, sleep } from '../../scripts/utils';



// ----------------------------------------------------------------------------
//   Component
// ----------------------------------------------------------------------------

export function VideoControlButtons({
  canStart,
  onStop,
  onRewind,

  // Play button
  canPlay = true,
  isPlaying,
  onPlay,

  // Captions
  hasCaptions = false,
  useCaptions,
  setCaptions,

  // Fullscreen
  canFullscreen = true,
  useFullscreen,
  setFullscreen,

  // Countdown
  canCountdown = false,
  useCountdown,
  setCountdown,
}) {

  // Custon styling for icon buttons
  const iconStyles = {
    'fontSize': 'large',
  }
  const buttonStyles = {
    sx: {
      'color': "#4447ad",
      '&:disabled': {
        color: 'rgba(223, 223, 223)',
      },
    }
  }

  return (
      <Stack direction="row"
        useFlexGap rowGap={{xs: 1, sm: 2}} columnGap={1}
        flex={1}
        marginX={2} paddingY={1}
        flexWrap={{ xs: "wrap", sm: "none" }}
        justifyContent="center"
      >

        {/* Pause (and Play) Button */}
        {
          canPlay
            ? (
              <IconToggleButton
                state={ isPlaying } onToggle={ isPlaying ? onStop : onPlay } {...buttonStyles}
                iconActive   = { <PauseIcon {...iconStyles} /> }
                iconInactive = { <PlayArrow {...iconStyles} /> }
              />
            )
            : (
              <IconButton disabled={ !canStart || !isPlaying } onClick={ onStop } {...buttonStyles} >
                <PauseIcon {...iconStyles} />
              </IconButton>
            )
        }

        {/* Rewind Button */}
        <IconButton disabled={ !onRewind } onClick={ () => onRewind(10) } {...buttonStyles}>
          <Replay10Icon {...iconStyles} />
        </IconButton>

        {/* Spacer */}
        <Box display={{ xs: "none", md: "unset" }} flex={1}></Box>

        {/* Countdown to Record */}
        <RenderIf condition={ canCountdown }>
          <IconToggleButton
            state={ useCountdown } onToggle={ setCountdown } {...buttonStyles}
            iconActive   = { <TimerOutlinedIcon    {...iconStyles} /> }
            iconInactive = { <TimerOffOutlinedIcon {...iconStyles} /> }
          />
        </RenderIf>

        {/* Captions */}
        <RenderIf condition={ hasCaptions }>
          <IconToggleButton
            state={ useCaptions } onToggle={ setCaptions } {...buttonStyles}
            iconActive   = { <ClosedCaptionIcon         {...iconStyles} /> }
            iconInactive = { <ClosedCaptionDisabledIcon {...iconStyles} /> }
          />
        </RenderIf>

        {/* Fullscreen */}
        <RenderIf condition={ canFullscreen }>
          <IconToggleButton
            state={ useFullscreen } onToggle={ setFullscreen } {...buttonStyles}
            iconActive   = { <FullscreenExitIcon {...iconStyles} /> }
            iconInactive = { <FullscreenIcon     {...iconStyles} /> }
          />
        </RenderIf>

      </Stack>
  )
}



// ----------------------------------------------------------------------------
//   Playback Bar
// ----------------------------------------------------------------------------


const CustomPlaybackSlider = styled(
  Slider,
  {
    shouldForwardProp: (prop) => !["color", "bgColor", "aheadColor", "maxProgress", "allowSkipAhead"].includes(prop)
  }
)(
  ({ theme, ...props }) => {

    const isCoarse    = useMediaQuery('(pointer: coarse)');
    const wcagMinTargetSize = '44px'

    // Extract props with appropriate fallback values
    const {
      height     = isCoarse ? "20px" : "12px",
      thumbWidth = 18,
      color      = "#b39fe7",
      aheadColor = "#7D79E0",
      bgColor    = "#4447ad",
      maxProgress = 100,
      allowSkipAhead = false,
    } = props;

    const trackColor = color;
    const railColor  = bgColor;
    const boxPadding = isCoarse ? `calc((${wcagMinTargetSize} - ${height}) / 2)` : 0;

    // Styles
    return {

      // The whole slider element
      color: trackColor,
      height,
      padding: 0,
      paddingTop:    boxPadding,
      paddingBottom: boxPadding,
      '@media (pointer: coarse)': {
        padding: 0,
        paddingTop:    boxPadding,
        paddingBottom: boxPadding,
      },
      borderRadius: 0,
      position: 'absolute',
      top: 0,
      marginLeft: `${thumbWidth / 2}px`,
      width: `calc(100% - ${thumbWidth}px)`,
      transition: 'none !important',

      // The thumb element
      '& .MuiSlider-thumb': {
        height:      `calc(${height} + (2 * 8px))`,
        width:       thumbWidth,
        paddingLeft:  `${ thumbWidth / 2 }px`,
        paddingRight: `${ thumbWidth / 2 }px`,
        backgroundColor: '#fff',
        border: '1px solid currentColor',
        borderRadius: "6px",
        boxShadow: 'none !important',
        transition: 'none !important',

        '&:hover': {
          boxShadow: 'none',
        },
      },

      // The slider "foreground"
      '& .MuiSlider-track': {
        height,
        border: '0px',
        transition: 'none !important',
      },

      // The slider "background"
      '& .MuiSlider-rail': {
        color: railColor,
        background: maxProgress === null ? railColor : hardLineGradient( aheadColor, railColor, maxProgress ),
        opacity: 1,
        height,
        border: '0px',
        transition: 'none !important',
        cursor: allowSkipAhead ? '' : 'not-allowed !important',
      },
    }
  }
);


export function VideoControlPlaybackBar({
  progress,
  maxProgress    = 100,
  useMaxProgress = false,
  onChange       = null,
  onSeekEnd      = null,
  stopAtEnd      = true,
  allowSkipAhead = false,
}) {

  // When pointer is coarse, need a wider bar
  const isCoarse = useMediaQuery('(pointer: coarse)');

  const height     = isCoarse ? "20px" : "12px";
  const paddingY   = isCoarse ? '8px' : 0;

  const trackColor = "#b39fe7";
  const aheadColor = "#6863d6";
  const railColor  = "#4447ad";
  const doneColor  = "#4caf50";

  const isDone = useMaxProgress && maxProgress === 100;
  const canInteract = !(isDone && stopAtEnd);

  return (
    <Box
      width="100%" height={height}
      marginTop={ isCoarse ? "-14px" : "-3px"}
      px={0} py={paddingY}
      zIndex={9999}
      position="relative"
    >
      <Box
        position="absolute"
        left={0}
        top={ isCoarse ? '12px' : 0 }
        height={ height }
        width="100%"
        sx={{ background: !canInteract ? doneColor : hardLineGradient(trackColor, (isDone) ? trackColor : railColor, 50) }}
      />

      <CustomPlaybackSlider
        aria-label   = "Video Progress"
        value        = {progress}
        defaultValue = {0}
        maxProgress  = { useMaxProgress ? maxProgress : null }
        step         = {0.001}
        color        = { canInteract ? trackColor : doneColor }
        aheadColor   = {aheadColor}
        bgColor      = {railColor}
        height       = {height}
        onChange          = {(e, v) => (canInteract && onChange ) ? onChange(v)  : null}
        onChangeCommitted = {(e, v) => (canInteract && onSeekEnd) ? onSeekEnd(v) : null}
        allowSkipAhead = {allowSkipAhead}
      />
    </Box>
  )
}



// ----------------------------------------------------------------------------
//   Countdown Play Button
// ----------------------------------------------------------------------------

export const VideoControlCountdownButton = forwardRef(
  function VideoControlCountdownButton({
    onClick = null,
    onStart = null,
    onCount = null,
    canStart     = false,
    useCountdown = true,
  }, ref )
{

  // Internal state
  const [ state, setState ] = useState(null);

  // Lock to prevent concurrent execution
  const isRunningRef = useRef(false);

  // Core countdown logic
  // IMPORTANT: Don't use directly - use onButtonClick to ensure countdown isn't triggered multiple times
  const runCountdown = async ({ start = 3, delay = 1000 }) => {

      // Trigger the onClick callback
      if (onClick) onClick();

      // Count down from the start value
      if (useCountdown) {
        for (let i = start; i > 0; i--) {
          setState(i);
          if (onCount) onCount(i);
          await sleep( delay );
        }
      }

      // Trigger the onStart callback & reset the state
      if (onStart) onStart();
      setState(null);
  }

  // Trigger logic - ensures countdown can't be re-triggered if already running
  const onButtonClick = ({ start = 3, delay = 1000, raise = false }) => {
    if (start <= 0) throw new Error("Invalid start value for countdown.");

    // Callback function that counts down from the given value
    return async () => {

      // Abort if already running
      // Throws an error if requested, but default behavior is to simply skip
      if (isRunningRef.current) {
        if (raise) {
          throw new Error('Countdown already in progress.');
        } else {
          return;
        }
      }

      // Abort if marked as unable to start
      // Throws an error if requested, but default behavior is to simply skip
      if (!canStart) {
        if (raise) {
          throw new Error('Cannot start countdown.');
        } else {
          return;
        }
      }

      // Set lock, run the main logic, and always release lock even if an error occurs
      isRunningRef.current = true;
      try {
        await runCountdown({ start, delay })
      } finally {
        isRunningRef.current = false;
      }
    }
  }

  // Expose a start countdown function on this element's ref
  // Allows parent elements to trigger the countdown flow
  useImperativeHandle(ref, () => ({
    'startCountdown': (raise = false) => {
      onButtonClick({ raise })();
    }
  }));

  const iconProps = {
    sx: { fontSize: "200px", color: "white" },
  }

  return (
    <IconButton onClick={ onButtonClick({ start: 3, delay: 1000, raise: false }) } >
      <RenderIf condition={state !== null} fallback={<PlayCircleFilledTwoToneIcon {...iconProps} />}>
        <CircleTwoToneIcon {...iconProps} />
        <Box height="100%" width="100%" top="0" left="0" position="absolute" textAlign="center" paddingTop="12px">
          <Typography color="white" fontSize="124px">{ state }</Typography>
        </Box>
      </RenderIf>
    </IconButton>
  )
})



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default {
  VideoControlButtons,
  VideoControlPlaybackBar,
  VideoControlCountdownButton,
}
